import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, DollarSign, Users, ChevronRight, Zap, ShieldAlert, Bot, Wrench, Layers, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Eyebrow, Spinner } from "@/components/tac";

const ALERT_ICON = { ai_review: Bot, payment: DollarSign, compliance: ShieldAlert, maintenance: Wrench };

export default function Dispatch() {
  const { user, workspace } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["overview"],
    queryFn: async () => (await api.get("/dispatch/overview")).data,
  });

  if (isLoading) return <div className="p-8"><Spinner label="Loading command center" /></div>;
  const c = data.counts;

  const stats = [
    { label: "Active jobs", value: data.active_jobs, icon: Activity, tone: "text-cyan-400" },
    { label: "Needs review", value: c.needs_review, icon: AlertTriangle, tone: "text-rose-400" },
    { label: "Captured revenue", value: `$${data.captured_revenue.toLocaleString()}`, icon: DollarSign, tone: "text-emerald-400" },
    { label: "Team", value: data.team_size, icon: Users, tone: "text-amber-400" },
  ];

  return (
    <div className="mx-auto max-w-[1700px] p-6">
      <Eyebrow>Dispatch command center</Eyebrow>
      <h1 className="mt-2 font-display text-3xl font-bold text-white">Today at a glance</h1>

      {user?.role === "owner" && workspace && !workspace.vertical && (
        <Link to="/templates" data-testid="onboarding-banner"
          className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 transition-colors hover:bg-amber-500/10">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/15 text-amber-400"><Layers className="h-5 w-5" /></span>
            <div>
              <div className="text-sm font-semibold text-white">Set up your industry</div>
              <div className="text-xs text-slate-400">Apply an industry pack to tailor trades, terminology and cost tags to your business.</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-semibold text-slate-950">Choose industry <ArrowRight className="h-3.5 w-3.5" /></span>
        </Link>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="card-tac p-5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">{s.label}</span>
              <s.icon className={`h-4 w-4 ${s.tone}`} />
            </div>
            <div className="mt-3 font-display text-3xl font-black text-white">{s.value}</div>
          </div>
        ))}
      </div>

      {data.crew_status?.length > 0 && (
        <div className="mt-8" data-testid="live-status-board">
          <div className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><Activity className="h-4 w-4 text-cyan-400" /> Live status board</div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {data.crew_status.map((j) => (
              <Link key={j.job_id} to={`/jobs/${j.job_id}`} data-testid={`crew-status-${j.job_id}`} className="card-tac p-4 transition-colors hover:border-cyan-500/40">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{j.title}</div>
                    <div className="truncate text-xs text-slate-400">{j.customer_name || " - "}</div>
                  </div>
                  <StatusPill status={j.status} />
                </div>
                <div className="mt-3 flex items-center justify-between font-mono-tac text-[10px] text-slate-500">
                  <span className="truncate">{j.assignees?.length ? j.assignees.join(", ") : "Unassigned"}</span>
                  <span>{j.last_gps_at ? `GPS ${j.last_gps_at.slice(11, 16)}` : (j.pretask_locked ? "safety ✓" : "")}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        {/* Dispatch board */}
        <div className="lg:col-span-8">
          <div className="card-tac">
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <h2 className="font-display text-lg font-semibold text-white">Recent jobs</h2>
              <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300">All jobs <ChevronRight className="h-4 w-4" /></Link>
            </div>
            <div className="divide-y divide-slate-800">
              {data.recent_jobs.map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`dispatch-job-${j.id}`}
                  className="flex items-center justify-between px-5 py-4 transition-colors hover:bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-slate-800 text-slate-400"><Zap className="h-4 w-4" /></div>
                    <div>
                      <div className="text-sm font-medium text-white">{j.title}</div>
                      <div className="font-mono-tac text-xs text-slate-500">{j.customer_name}</div>
                    </div>
                  </div>
                  <StatusPill status={j.status} />
                </Link>
              ))}
              {!data.recent_jobs.length && <div className="px-5 py-10 text-center text-sm text-slate-500">No jobs yet.</div>}
            </div>
          </div>

          {/* Pipeline */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {Object.entries(c).map(([k, v]) => (
              <div key={k} className="card-tac p-4 text-center">
                <div className="font-display text-2xl font-black text-white">{v}</div>
                <StatusPill status={k} className="mt-2" />
              </div>
            ))}
          </div>
        </div>

        {/* Alert queue */}
        <div className="lg:col-span-4">
          <div className="card-tac">
            <div className="border-b border-slate-800 px-5 py-4">
              <h2 className="font-display text-lg font-semibold text-white">Review queue</h2>
              <p className="mt-0.5 font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Alert-driven</p>
            </div>
            <div className="space-y-3 p-4">
              {data.alerts.map((a, i) => {
                const Icon = ALERT_ICON[a.kind] || AlertTriangle;
                return (
                  <div key={i} data-testid={`alert-${a.kind}`}
                    className={`flex items-start gap-3 rounded-xl border p-4 ${a.severity === "high" ? "border-rose-500/40 bg-rose-950/30" : "border-amber-500/40 bg-amber-950/20"}`}>
                    <Icon className={`mt-0.5 h-5 w-5 ${a.severity === "high" ? "text-rose-400" : "text-amber-400"}`} />
                    <div className="flex-1">
                      <div className="text-sm text-white">{a.label}</div>
                      <Link to={a.kind === "ai_review" ? "/review" : "/jobs"} className="mt-1 inline-block text-xs text-cyan-400 hover:text-cyan-300">Resolve →</Link>
                    </div>
                  </div>
                );
              })}
              {!data.alerts.length && <div className="py-8 text-center text-sm text-slate-500">All clear. No open alerts.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
